/* eslint-disable @typescript-eslint/no-explicit-any -- SDK generics are deliberately broad */
import { useEffect, useMemo, useRef } from 'react';
import type {
  DbConnectionBuilder,
  DbConnectionImpl,
  ManagedConnectionState,
} from 'spacetimedb';
import { ConnectionManager } from 'spacetimedb';
import { getReconnectThrottle } from './reconnectThrottle';

/**
 * Catch silent socket drops that don't surface a `connectionError`.
 *
 * Symptom: the server closes a WS with `code=1006 reason=""` (the most
 * common shape after a redeploy or a backgrounded tab) and the SDK
 * flips `isActive: true → false` *without* populating
 * `connectionError`. The auto-reconnect path short-circuits on `!err`
 * and the socket sits dead — only a page reload recovers.
 *
 * This hook watches for that exact `isActive` transition. It debounces
 * for `debounceMs` (default 1000ms) to let any real error populate; if
 * the error path takes over during that window the rebuild is
 * cancelled. A `cooldownMs` (default 30s) gap between rebuilds
 * prevents storms when the underlying cause is sticky (e.g. server
 * rejecting the JWT) — repeated silent rebuilds would never converge.
 *
 * `beforeRebuild` is awaited right before the rebuild so we refresh
 * the JWT (the dropped socket may have been on an expired token, and
 * reconnecting with that same token just earns another 1006).
 */
export type SilentDisconnectWatchdogOptions = {
  debounceMs?: number;
  cooldownMs?: number;
  /** Awaited before the rebuild. Return value is ignored. */
  beforeRebuild?: () => Promise<unknown> | unknown;
};

export function useSilentDisconnectWatchdog<T extends DbConnectionImpl<any>>(
  poolKey: string,
  state: ManagedConnectionState,
  builderFactory: () => DbConnectionBuilder<T>,
  logLabel: string,
  options: SilentDisconnectWatchdogOptions = {},
): void {
  const debounceMs = options.debounceMs ?? 1000;
  const cooldownMs = options.cooldownMs ?? 30_000;

  const throttle = useMemo(() => getReconnectThrottle(poolKey), [poolKey]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const factoryRef = useRef(builderFactory);
  factoryRef.current = builderFactory;
  const beforeRebuildRef = useRef(options.beforeRebuild);
  beforeRebuildRef.current = options.beforeRebuild;

  useEffect(() => {
    // Active: clear any debounced rebuild and remember we've seen this
    // pool healthy at least once.
    if (state.isActive) {
      throttle.everActive = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Not our job: the error path owns connectionError cases, and the
    // initial-connect path owns never-been-active entries.
    if (state.connectionError) return;
    if (!throttle.everActive) return;
    if (timerRef.current) return;

    // Cooldown: a sticky problem would otherwise produce a tight
    // silent-rebuild storm. Let the SDK eventually surface
    // connectionError so useAutoReconnect's backoff inherits the
    // problem instead.
    if (Date.now() - throttle.lastSilentRebuildAt < cooldownMs) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      throttle.lastSilentRebuildAt = Date.now();
      void (async () => {
        await beforeRebuildRef.current?.();
        console.info(`[stdb:${logLabel}] silent disconnect — rebuilding`);
        ConnectionManager.rebuild(poolKey, factoryRef.current());
      })();
    }, debounceMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [throttle, state.isActive, state.connectionError, poolKey, logLabel, debounceMs, cooldownMs]);
}
